import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/shared/config/queryKeys';
import { AppError } from '@/shared/lib/repository/errors';
import { useAuth } from '@/features/auth';
import { commentsRepository } from './commentsRepository';
import { toggleLikeInTree } from '../model/tree';
import type { CommentNode } from '../model/types';

export function useComments(articleId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: queryKeys.comments(articleId),
    queryFn: () => commentsRepository.listForArticle(articleId, user?.id),
  });
}

export function useAddComment(articleId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: string; parentId?: string | null }) => {
      if (!user) throw new AppError('Sign in to comment');
      return commentsRepository.add({
        articleId,
        authorId: user.id,
        body: input.body,
        parentId: input.parentId ?? null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.comments(articleId) });
    },
  });
}

export function useToggleCommentLike(articleId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;
  const key = queryKeys.comments(articleId);

  return useMutation({
    mutationFn: async ({ commentId, liked }: { commentId: string; liked: boolean }) => {
      if (!userId) throw new AppError('Sign in to like');
      if (liked) await commentsRepository.unlike(commentId, userId);
      else await commentsRepository.like(commentId, userId);
    },
    onMutate: async ({ commentId }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const prev = queryClient.getQueryData<CommentNode[]>(key);
      queryClient.setQueryData<CommentNode[]>(key, (old) =>
        old ? toggleLikeInTree(old, commentId) : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(key, ctx.prev);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
